import React, { MouseEvent, useState } from "react";
import ReactDOM from "react-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as _ from "lodash";

import { UiUtils } from "../common/uiUtils";
import { ModelApi } from "../api/modelApi";
import { ViewerUtils } from "../common/viewerUtils";
import * as Upload from "./upload";
import { FormField, NumberField, SelectField, CheckboxField } from "../common/forms";

interface MetadataFormProps {
    projectModel: any;
    items: Upload.FileMap;
    processing: boolean;
    finalizeUpload: (crs: Upload.CommitResponse) => void;
    cancelUpload: () => void;
    modelSettingsIds: { label: string, value: string}[];
}

interface MetadataFormState {
    queryId: string;
}

const parseGroups = (groupStr: string): string[] => {
    return groupStr.split(",").map(p => p.trim()).filter(p => p.length > 0);
}

const requiredMetadata = ["customer", "manager", "projectId", "projectName", "validMonths"];

const serverIdToFieldName = (serverId: string): string => {
    return `file_${serverId.replace(".", "_")}`;
}

const makeInitialValues = (items: Upload.FileMap) => {
    const vals = {
        customer: "",
        manager: "",
        projectId: "",
        projectName: "",
        validMonths: 12,
        modelSettingsId: "",
        authGroups: "",
        analyticsAuthGroups: "",
        editGroups: ""
    };
    for (let serverId in items) {
        const item = {
            name: items[serverId].filename.split(".").slice(0, -1).join("."),
            modelSettingsId: "",
            modelAuthGroups: "",
            modelAnalyticsAuthGroups: "",
            modelEditGroups: ""
        }
        vals[serverIdToFieldName(serverId)] = item;
    }

    return vals;
}

const makeProjectData = (values, projectSelection, projectModel) => {
    if (projectModel != null) {
        return { existingProjectId: projectModel.project.id, existingQueryId: _.get(projectModel, "revisionModel.queryId") };
    }

    if (projectSelection === "new") {
        return _.pick(values, ...requiredMetadata);
    } else {
        return { existingProjectId: "examples" };
    }
}

const makeModelMetadata = (item: Upload.UploadItem, values) => {
    const fieldNamePrefix = serverIdToFieldName(item.serverId);
    const modelAuthGroups = values[fieldNamePrefix].modelAuthGroups || values.authGroups;
    const modelAnalyticsAuthGroups = values[fieldNamePrefix].modelAnalyticsAuthGroups || values.analyticsAuthGroups;
    const modelEditGroups = values[fieldNamePrefix].modelEditGroups || values.editGroups;
    return {
        serverId: item.serverId,
        modelName: values[fieldNamePrefix].name,
        modelSettingsId: values[fieldNamePrefix].modelSettingsId || values.modelSettingsId,
        texturedModel: values[fieldNamePrefix].texturedModel,
        authGroups: parseGroups(modelAuthGroups),
        analyticsAuthGroups: parseGroups(modelAnalyticsAuthGroups),
        editGroups: parseGroups(modelEditGroups)
    }
}

const FileCard = (props) => {
    const fieldNamePrefix = serverIdToFieldName(props.item.serverId);
    return <div className="card">
               <div className="card-body">
                   <h6 className="card-title">{props.item.filename}</h6>
                   <FormField name={`${fieldNamePrefix}.name`} label="Model name *"/>
                   <SelectField name={`${fieldNamePrefix}.modelSettingsId`} label="Model settings ID" values={props.modelSettingsIds}/>
                   <CheckboxField name={`${fieldNamePrefix}.texturedModel`} label="Textured model" description="If checked different lighting settings will be used to enhance the model visuals."/>
                   {!props.exampleProject &&
                <div>
                <FormField name={`${fieldNamePrefix}.modelAuthGroups`} label="Model authorization groups"
                    description="Comma-separated (,) list of groups that will be able to view the models. Leave blank if the models should be public and visible to all users." />
                <FormField name={`${fieldNamePrefix}.modelAnalyticsAuthGroups`} label="Model analytics groups" description="List of groups that will be able to view the model's Google Analytics if the model is public." />
                <FormField name={`${fieldNamePrefix}.modelEditGroups`} label="Model edit groups"
                    description="Comma-separated (,) list of groups that will be able to edit the model's data. Leave blank if the models should be editable to all users." />
                </div>
            }
               </div>
           </div>;
}

const MetadataForm: React.SFC<MetadataFormProps> = (props) => {
    const [project, setProject] = useState("new");
    return (
        <Formik
            enableReinitialize={true}
            initialValues={makeInitialValues(props.items)}
            onSubmit={async (values, actions) => {
                const projectData = makeProjectData(values, project, props.projectModel);
                const meta = {
                    ...projectData,
                    projectAuthGroups: parseGroups(values.authGroups),
                    models: _.values(props.items).map(i => makeModelMetadata(i, values))
                };

                const { data } = await ModelApi.commitUploadedModels(meta);
                props.finalizeUpload(data);
                actions.setSubmitting(false);
            }}
            validate={(values: any) => {
                const errors: any = {};

                if (props.projectModel == null && project === "new") {
                    for (let req of requiredMetadata) {
                        if (!values[req]) {
                            errors[req] = "Required";
                        }
                    }
                }

                const files = _.keys(values).filter(k => _.startsWith(k, "file_"));
                for (let f of files) {
                    if (!values[f]) {
                        errors[f] = "Required";
                    }
                }

                return errors;
            }}
            render={({ values, errors, status, touched, isSubmitting, handleReset, setValues }) => (
                <Form className="container pt-3" style={{ width: "auto" }}>
                    <div className="row">
                        <h5>Project information</h5>
                    </div>

                    {props.projectModel != null &&
                        <div className="form-group row">
                            <label className="col-formlabel col-sm-3">Project</label>
                            <div className="col-sm-6">
                            <span>{props.projectModel.project.name}</span>
                            </div>
                        </div>}

                    {props.projectModel != null && props.projectModel.revisionModel != null &&
                        <div className="form-group row">
                            <label className="col-formlabel col-sm-3">New revision for model</label>
                            <div className="col-sm-6">
                                <span>{props.projectModel.revisionModel.name}</span>
                            </div>
                        </div>}

                    {props.projectModel == null &&
                    <div className="form-group row">
                        <label className="col-formlabel col-sm-3">Project</label>
                        <div className="col-sm-6">
                            
                                <select
                                className="form-control"
                                name="project"
                                value={project}
                                onChange={e => setProject(e.target.value)}>
                                <option value="new" label="Create new project"/>
                                <option value="examples" label="Add models to example project"/>
                                </select>
                        </div>
                    </div>}

                    {props.projectModel == null && project === "new" &&
                        <div>
                            <FormField name="customer" label="Customer *" />
                            <FormField name="manager" label="Manager *" />
                            <FormField name="projectId" label="Project identifier *" />
                            <FormField name="projectName" label="Project name *" />
                            <NumberField name="validMonths" label="Validity (months) *" />
                            <SelectField name="modelSettingsId" label="Model settings ID" values={props.modelSettingsIds} />
                            <FormField name="authGroups" label="Project and model authorization groups"
                            description="Comma-separated (,) list of groups that will be able to view the project and models. Leave blank if the models should be public and visible to all users." />
                            <FormField name="analyticsAuthGroups" label="Model analytics groups" description="List of groups that will be able to view the model's Google Analytics if the model is public." />
                            <FormField name="editGroups" label="Model edit groups" description="Comma-separated (,) list of groups that will be able to edit the model's data. Leave blank if the models should be editable to all users." />
                        </div>
                    }
                    {props.projectModel == null && project === "examples" &&
                        <div>
                        <SelectField name="modelSettingsId" label="Model settings ID" values={props.modelSettingsIds}/>
                        </div>
                    }

                    <div className="accordion" id="modelAccordion">
                        <div className="card">
                            <div className="card-header" id="modelHeading">
                                <h2 className="mb-0">
                                    <button className="btn btn-link" type="button" data-toggle="collapse" data-target="#modelList" aria-expanded="true" aria-controls="modelList">
                                        <h5>Model details</h5>
                                    </button>
                                </h2>
                            </div>

                            <div id="modelList" className="collapse" aria-labelledby="modelHeading" data-parent="#modelAccordion">
                                {!props.processing && _.values(props.items).map(i => <FileCard key={i.serverId} item={i}
                                    modelSettingsIds={props.modelSettingsIds} exampleProject={project === "examples"} />)}
                            </div>
                        </div>
                    </div>

                    <button className="btn btn-primary mt-1" type="submit" disabled={props.processing || isSubmitting}>
                        Create models
                    </button>

                    <button className="btn btn-secondary ml-1 mt-1" type="button" disabled={props.processing || isSubmitting} onClick={props.cancelUpload}>
                        Cancel
                    </button>
                </Form>
            )}/>
    );
}

export { MetadataForm };